import React, { FC } from 'react';
import { Helmet } from 'react-helmet';
import { withPrefix } from 'gatsby';

import useSiteMetadata from '../../hooks/use-site-metadata';

interface DrupalMetatag {
  key?: string | null;
  value?: string | null;
}

interface DrupalNode {
  status?: boolean | null;
  entityUrl?: {
    path?: string | null;
  } | null;
  entityLanguage?: {
    id?: string | null;
  } | null;
}

interface Props {
  entityMetatags?: DrupalMetatag[] | null;
  entityTranslations?: DrupalNode[] | null;
  shortLangPrefix?: string;
  pageType?: string;
  location: {
    pathname?: string | null;
  };
}

const SEO: FC<Props> = ({
  entityMetatags = [],
  entityTranslations = [],
  shortLangPrefix = 'en',
  pageType,
  location
}) => {
  const { siteUrl, title, favicon } = useSiteMetadata();
  let overrideCanonical = false;
  const tagsWithName = ['description', 'keywords', 'robots'];

  return (
    <Helmet>
      <title>{title}</title>

      {entityMetatags?.map(({ key, value }) => {
        let tag;
        if (key === 'title') {
          tag = <title key={key}>{value}</title>;
        } else if (key === 'canonical') {
          tag = <link key={key} rel={key} href={value!} />;
          overrideCanonical = true;
        } else if (tagsWithName.includes(key!)) {
          tag = <meta key={key!} name={key!} content={value!} />;
        } else {
          tag = <meta key={key!} property={key!} content={value!} />;
        }

        return tag;
      })}

      {!overrideCanonical && (
        <link rel="canonical" href={`${siteUrl}${location.pathname}`} />
      )}

      <link rel="shortcut icon" type="image/png" href={favicon} />

      {entityTranslations
        ?.filter(({ status }) => status)
        .map(({ entityUrl, entityLanguage }) => {
          let href = `${siteUrl}${withPrefix(entityUrl?.path!)}`;

          if (entityLanguage!.id === 'en' && pageType === 'homepage') {
            href = siteUrl;
          }

          return (
            <link
              key={entityLanguage!.id!}
              href={href}
              rel="alternate"
              hrefLang={entityLanguage!.id!}
            />
          );
        })}

      <html
        lang={shortLangPrefix}
        data-lang={shortLangPrefix}
        className={pageType ? `pageType__${pageType}` : ''}
      />
    </Helmet>
  );
};

export default SEO;
